import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { showInfoAlert } from '../utils/alertFunction';
import { getSpeechLocale, getTexts } from '../utils/i18n';
import { speakWithAzure, SpeakControl } from '../services/azureSpeech';
import '../styles/voicechat.css';

interface VoiceChatProps {
    isActive: boolean;
    onClose: () => void;
    sendMessage: (message: string) => Promise<string | undefined>;
}

export interface VoiceChatHandle {
    // Disaridan (ör. yazili mesaj gonderildiginde) devam eden okumayi kesip
    // yeni metni ayni sesli sohbet akisina sokmak icin.
    interruptWithText: (text: string) => void;
}

type VoiceChatPhase = 'listening' | 'thinking' | 'speaking';

const SILENCE_TIMEOUT_MS = 1500;

const normalizeForCompare = (s: string) =>
    s
        .toLowerCase()
        .replace(/[.,!?;:'"()]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

// Bag-of-words orani degil, SIRALI alt-dizi eslesme orani kullanilir: gercek
// yanki, okunan metnin bir parcasi oldugu icin kelimeleri ayni sirada gecer.
// Kullanicinin yeni ve alakasiz bir cumlesi ortak kelimeler paylassa bile
// (bu botta "randevu", "brans" gibi kelimeler surekli tekrar eder) o kelimeler
// ayni sirada gecmedigi surece yuksek oran almaz.
const ECHO_WORD_OVERLAP_THRESHOLD = 0.7;
// Tek kelimelik parcalar (ör. "size") her zaman yanittaki bir kelimeyle
// eslesir ve yanki/gercek ayrimini guvenilmez kilar; barge-in icin en az bu
// kadar kelime taninmadan karar verilmez.
const MIN_BARGE_IN_WORDS = 2;
// Recognition yeniden basladiginda ayni cumlenin tekrar "final" gelme ihtimaline
// karsi, bu sure icinde birebir ayni metin ikinci kez islenmez.
const DUPLICATE_TRANSCRIPT_WINDOW_MS = 8000;

const orderedWordOverlapRatio = (heardWords: string[], speakingWords: string[]): number => {
    if (heardWords.length === 0) return 0;
    let searchFrom = 0;
    let matched = 0;
    for (const word of heardWords) {
        const foundIdx = speakingWords.indexOf(word, searchFrom);
        if (foundIdx !== -1) {
            matched++;
            searchFrom = foundIdx + 1;
        }
    }
    return matched / heardWords.length;
};

// Mai konusurken mikrofon acik kaldigi icin hoparlorden sizan kendi sesini
// duyup "kullanici konustu" sanabiliyor. Taninan metin, o an okunan yanitin
// kelimeleriyle SIRALI olarak buyuk oranda ortusuyorsa bu bir yanki kabul
// edilip yoksayilir; gercekten farkli seyler soyleniyorsa barge-in tetiklenir.
const isLikelyEcho = (heard: string, speakingText: string): boolean => {
    if (!speakingText) return false;
    const heardWords = normalizeForCompare(heard).split(' ').filter(Boolean);
    if (heardWords.length === 0) return false;
    const speakingWords = normalizeForCompare(speakingText).split(' ').filter(Boolean);
    return orderedWordOverlapRatio(heardWords, speakingWords) >= ECHO_WORD_OVERLAP_THRESHOLD;
};

const VoiceChat = forwardRef<VoiceChatHandle, VoiceChatProps>(({ isActive, onClose, sendMessage }, ref) => {
    const texts = getTexts();
    const [phase, setPhase] = useState<VoiceChatPhase>('listening');
    const phaseRef = useRef<VoiceChatPhase>('listening');
    const activeRef = useRef(false);
    const recognitionRef = useRef<any>(null);
    const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const speakControlRef = useRef<SpeakControl | null>(null);
    const processingRef = useRef(false);
    // Su an Mai'nin okumakta oldugu tam metin - yanki tespiti icin.
    const speakingTextRef = useRef('');
    // Devam eden (henuz gonderilmemis) final transcript. Recognition tek bir
    // oturum boyunca (listening + thinking + speaking) surekli calistigi icin
    // ref olarak tutuluyor.
    const transcriptRef = useRef('');
    // Recognition, tarayicinin ic zaman asimlariyla ara sira kendini yeniden
    // baslatiyor (bkz. onend). Bu yeniden baslama sirasinda son cumle bazen
    // ikinci kez "final" olarak tekrar teslim ediliyor ve ayni mesaj/yanit
    // ciftlenmis sekilde gorunuyor. Son islenen metni kisa sure hatirlayip
    // birebir tekrarini yoksayarak bu ciftlenmeyi engelliyoruz.
    const lastProcessedRef = useRef<{ text: string; time: number } | null>(null);

    const setPhaseAll = (value: VoiceChatPhase) => {
        phaseRef.current = value;
        setPhase(value);
    };

    const stopRecognition = () => {
        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
        if (recognitionRef.current) {
            recognitionRef.current.onend = null;
            recognitionRef.current.onresult = null;
            recognitionRef.current.onerror = null;
            try {
                recognitionRef.current.stop();
            } catch {
                // yoksayilir
            }
            recognitionRef.current = null;
        }
    };

    const stopSpeaking = () => {
        speakControlRef.current?.stop();
        speakControlRef.current = null;
        speakingTextRef.current = '';
    };

    const stopAll = () => {
        stopRecognition();
        stopSpeaking();
    };

    // text: kullanicidan gelen (sesli veya yazili) nihai mesaj.
    const processTranscript = async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || processingRef.current) return;

        const last = lastProcessedRef.current;
        if (
            last &&
            normalizeForCompare(last.text) === normalizeForCompare(trimmed) &&
            Date.now() - last.time < DUPLICATE_TRANSCRIPT_WINDOW_MS
        ) {
            // Recognition yeniden baslarken ayni cumleyi tekrar "final" olarak
            // gonderdi - ikinci kez isleme almadan yoksay.
            transcriptRef.current = '';
            return;
        }
        lastProcessedRef.current = { text: trimmed, time: Date.now() };

        processingRef.current = true;
        transcriptRef.current = '';
        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
        // Kullanici araya girdiyse (yazarak ya da konusarak) devam eden okuma kesilir.
        stopSpeaking();
        setPhaseAll('thinking');

        const responseText = await sendMessage(trimmed);
        processingRef.current = false;

        if (!activeRef.current) return;

        if (responseText === undefined) {
            onClose();
            return;
        }

        setPhaseAll('speaking');
        speakingTextRef.current = responseText;
        const control = speakWithAzure(responseText);
        speakControlRef.current = control;
        try {
            await control.promise;
        } catch (error) {
            console.error('Azure TTS hatasi:', error);
        }
        if (speakControlRef.current === control) {
            speakControlRef.current = null;
        }
        speakingTextRef.current = '';

        if (activeRef.current) {
            setPhaseAll('listening');
        }
    };

    useImperativeHandle(ref, () => ({
        interruptWithText: (text: string) => {
            if (!activeRef.current) return;
            void processTranscript(text);
        }
    }));

    const startListening = () => {
        if (!activeRef.current) return;

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            showInfoAlert(texts.info, texts.speechNotSupported);
            onClose();
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = getSpeechLocale();

        recognition.onresult = (event: any) => {
            let interimTranscript = '';
            let finalChunk = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalChunk += transcript + ' ';
                } else {
                    interimTranscript += transcript;
                }
            }

            if (phaseRef.current === 'speaking') {
                const heard = (finalChunk + interimTranscript).trim();
                if (!heard) return;

                const heardWordCount = normalizeForCompare(heard).split(' ').filter(Boolean).length;

                if (heardWordCount < MIN_BARGE_IN_WORDS || isLikelyEcho(heard, speakingTextRef.current)) {
                    // Cok kisa parcalar yanki/gercek ayrimi icin guvenilmez, ya da
                    // Mai'nin kendi sesinin mikrofona sizmasi - yoksay.
                    return;
                }

                // Gercek kullanici araya girdi (barge-in): okumayi kes, dinlemeye gec.
                stopSpeaking();
                setPhaseAll('listening');
                transcriptRef.current += finalChunk;
            } else if (phaseRef.current === 'listening') {
                transcriptRef.current += finalChunk;
            } else {
                // 'thinking': yanit beklenirken gelen ses yoksayilir.
                return;
            }

            if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
            }

            silenceTimeoutRef.current = setTimeout(() => {
                void processTranscript(transcriptRef.current + interimTranscript);
            }, SILENCE_TIMEOUT_MS);
        };

        recognition.onerror = (event: any) => {
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                showInfoAlert(texts.info, texts.permissionDeniedText);
                onClose();
            } else if (event.error === 'no-speech' || event.error === 'aborted') {
                // sessizlik - dinlemeye devam
            } else {
                console.error('Sesli sohbet tanima hatasi:', event.error);
            }
        };

        recognition.onend = () => {
            // Tarayici beklenmedik sekilde dinlemeyi durdurdugunda
            // (kullanicinin sessiz kalmasi taraydiicilar ozelinde farkli sekillerde sonlanabiliyor)
            // mikrofon oturumu (listening/thinking/speaking farketmeksizin) otomatik yeniden baslatilir.
            if (activeRef.current) {
                startListening();
            }
        };

        recognitionRef.current = recognition;
        recognition.start();
    };

    useEffect(() => {
        activeRef.current = isActive;
        if (isActive) {
            transcriptRef.current = '';
            lastProcessedRef.current = null;
            setPhaseAll('listening');
            startListening();
        } else {
            stopAll();
        }
        return () => {
            stopAll();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    if (!isActive) return null;

    return (
        <div className="voice-chat-overlay">
            <video
                className="voice-chat-video"
                src="/images/sesli.mp4"
                autoPlay
                loop
                muted
                playsInline
            />
            <div className="voice-chat-status">
                {phase === 'listening' && texts.voiceChatListening}
                {phase === 'thinking' && texts.voiceChatThinking}
                {phase === 'speaking' && texts.voiceChatSpeaking}
            </div>
            <button className="voice-chat-close" onClick={onClose} type="button" title={texts.close}>
                <i className="fas fa-times" />
            </button>
        </div>
    );
});

export default VoiceChat;
